import React, { useEffect, useState } from 'react';

import { FileExcelOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Modal,
  Row,
  Select,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ApiErrorData } from 'models/ApiResponse';
import {
  ExportLeadsParams,
  LeadTagMatch,
  ProfileStatusCode,
  ProfileStatusEntry,
} from 'models/Lead';

import { LeadService } from 'services/LeadService';

import appI18n from '../../../config/i18n';
import LeadTagsFilter from './LeadTagsFilter';
import StatusIndicator from './StatusIndicator';
import {
  OTHER_UNIVERSITY_VALUE,
  getCityLabelKey,
  getRegionLabelKey,
} from './leadHelpers';
import { useLeadCatalogs } from './useLeadCatalogs';

const COUNTRY_OPTIONS = [{ value: 'CL' }, { value: 'PE' }, { value: 'CO' }];

/**
 * Universidad / región / ciudad están implementados y listos, pero ocultos
 * hasta confirmar si el export debe ofrecerlos. Mientras esté en `false` no se
 * renderizan, no se cargan sus catálogos y no viajan en la petición.
 * Ponerlo en `true` es lo único necesario para habilitarlos.
 */
const SHOW_LOCATION_FILTERS = false;

interface ExportLeadsModalProps {
  isOpen: boolean;
  country: string;
  statuses: ProfileStatusEntry[];
  handleModalCancel: () => void;
}

function ExportLeadsModal({
  isOpen,
  country,
  statuses,
  handleModalCancel,
}: ExportLeadsModalProps) {
  const { Text } = Typography;
  const { RangePicker } = DatePicker;
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const [messageApi, contextHolder] = message.useMessage();
  const dateFormat: string = useSelector(
    (state: any) => state.intl?.dateFormat
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [countrySelection, setCountrySelection] = useState<string>(country);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null
  );
  const [availabilityDateRange, setAvailabilityDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | null
  >(null);
  const [statusSelection, setStatusSelection] = useState<ProfileStatusCode[]>(
    []
  );
  // Universidades del catálogo y/o OTHER_UNIVERSITY_VALUE
  const [universitySelection, setUniversitySelection] = useState<number[]>([]);
  // Región: selección única (string con padding significativo, p. ej. "05")
  const [regionSelection, setRegionSelection] = useState<string | undefined>(
    undefined
  );
  // Ciudades/comunas: ids integer de GET /cities
  const [citySelection, setCitySelection] = useState<number[]>([]);
  // Etiquetas: ids del catálogo del país elegido en este modal.
  const [tagSelection, setTagSelection] = useState<number[]>([]);
  const [tagMatch, setTagMatch] = useState<LeadTagMatch>('any');

  const {
    universities,
    universitiesLoading,
    regions,
    regionsLoading,
    cities,
    citiesLoading,
  } = useLeadCatalogs(
    countrySelection,
    regionSelection,
    isOpen && SHOW_LOCATION_FILTERS,
    (catalog, error) =>
      messageApi.error(
        (error.message as string) ||
          t(`scenes.leads.scene.errors.fetch_${catalog}`),
        3.5
      )
  );

  useEffect(() => {
    if (isOpen) {
      setCountrySelection(country);
      setDateRange(null);
      setAvailabilityDateRange(null);
      setStatusSelection([]);
      setUniversitySelection([]);
      setRegionSelection(undefined);
      setCitySelection([]);
      setTagSelection([]);
      setTagMatch('any');
    }
  }, [isOpen, country]);

  const handleCountryChange = (value: string) => {
    setCountrySelection(value);
    // Los catálogos de universidades, regiones, ciudades y etiquetas son por país: al
    // cambiarlo, lo seleccionado deja de existir en el nuevo catálogo.
    setUniversitySelection([]);
    setRegionSelection(undefined);
    setCitySelection([]);
    setTagSelection([]);
  };

  const handleRegionChange = (value?: string) => {
    setRegionSelection(value);
    // Las ciudades pertenecen a la región anterior: dejan de aplicar.
    setCitySelection([]);
  };

  // Prioriza el label del backend (ya resuelto por idioma); cae a i18n local y luego al code.
  const getStatusLabel = (
    code: string,
    backendLabel?: string | null
  ): string => {
    if (backendLabel && backendLabel.trim().length > 0) return backendLabel;
    return t(`scenes.leads.scene.status_codes.${code}`, { defaultValue: code });
  };

  const statusOptions = statuses.map((s) => ({
    value: s.code,
    label: (
      <StatusIndicator
        color={s.color}
        label={getStatusLabel(
          s.code,
          i18n.language === 'en' ? s.labelEn : s.labelEs
        )}
      />
    ),
  }));

  const universityOptions = [
    ...universities.map((u) => ({ value: u.id, label: u.name })),
    {
      value: OTHER_UNIVERSITY_VALUE,
      label: t('scenes.leads.scene.filters.university_other'),
    },
  ];

  const regionOptions = regions.map((r) => ({
    value: r.regionId,
    label: r.regionName,
  }));

  // El filtro `cityIds` toma el `id` integer, NO el `cityId` varchar.
  const cityOptions = cities.map((c) => ({ value: c.id, label: c.cityName }));

  // La nomenclatura de región/ciudad cambia por país (Región/Comuna en Chile,
  // Departamento/Distrito en Perú, ...).
  const regionLabel = t(
    `scenes.leads.scene.filters.${getRegionLabelKey(countrySelection)}`
  );
  const cityLabel = t(
    `scenes.leads.scene.filters.${getCityLabelKey(countrySelection)}`
  );

  const countryOptions = COUNTRY_OPTIONS.map((opt) => {
    const code = opt.value.toLowerCase();
    return {
      value: opt.value,
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <img
            src={`https://flagcdn.com/16x12/${code}.png`}
            srcSet={`https://flagcdn.com/32x24/${code}.png 2x`}
            width={16}
            height={12}
            alt=""
            style={{ display: 'block' }}
          />
          <span>
            {t(`scenes.leads.scene.countries.${opt.value}`, opt.value)}
          </span>
        </span>
      ),
    };
  });

  const getBrowserTimezone = (): string | undefined => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch (e) {
      return undefined;
    }
  };

  const handleExport = async () => {
    setLoading(true);
    const messageKey = 'exporting-leads';
    messageApi.loading({
      content: t('scenes.leads.scene.export.messages.exporting'),
      key: messageKey,
      duration: 0,
    });

    const params: ExportLeadsParams = { country: countrySelection };
    const tz = getBrowserTimezone();
    if (tz) params.timezone = tz;
    if (dateRange) {
      params.createdFrom = dateRange[0].format('YYYY-MM-DD');
      params.createdTo = dateRange[1].format('YYYY-MM-DD');
    }
    if (availabilityDateRange) {
      params.availabilityUpdatedFrom =
        availabilityDateRange[0].format('YYYY-MM-DD');
      params.availabilityUpdatedTo =
        availabilityDateRange[1].format('YYYY-MM-DD');
    }
    if (statusSelection.length > 0) {
      params.statuses = statusSelection;
    }
    if (universitySelection.length > 0) {
      // OTHER_UNIVERSITY_VALUE (-1) representa "Otros" → se manda null al backend
      params.universityIds = universitySelection.map((id) =>
        id === OTHER_UNIVERSITY_VALUE ? null : id
      );
    }
    if (regionSelection) {
      // El backend acepta varias regiones, pero la UI es de selección única.
      params.regionIds = [regionSelection];
    }
    if (citySelection.length > 0) {
      params.cityIds = citySelection;
    }
    if (tagSelection.length > 0) {
      params.tagIds = tagSelection;
      params.tagMatch = tagMatch;
    }

    const response = await LeadService.exportLeads(params);

    if (response.success && response.data) {
      const data = response.data as { blob: Blob; filename: string };
      const url = window.URL.createObjectURL(data.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      messageApi.destroy(messageKey);
      messageApi.success(t('scenes.leads.scene.export.messages.success'));
      handleModalCancel();
    } else {
      const error = response.data as ApiErrorData;
      messageApi.error({
        content:
          (error?.message as string) ||
          t('scenes.leads.scene.export.errors.export_failed'),
        key: messageKey,
      });
    }

    setLoading(false);
  };

  return (
    <Modal
      title={
        <span>
          <FileExcelOutlined /> {t('scenes.leads.scene.export.modal_title')}
        </span>
      }
      open={isOpen}
      onCancel={handleModalCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={500}
    >
      {contextHolder}
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            {t('scenes.leads.scene.export.labels.country')}{' '}
            <span style={{ color: '#ff4d4f' }}>*</span>
          </Text>
          <Select
            value={countrySelection}
            onChange={(value) => handleCountryChange(value as string)}
            options={countryOptions}
            style={{ width: '100%' }}
          />
        </Col>

        <Col xs={24}>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            {t('scenes.leads.scene.export.labels.date_range')}
          </Text>
          <RangePicker
            style={{ width: '100%' }}
            format={dateFormat}
            value={dateRange}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([
                  dates[0] as dayjs.Dayjs,
                  dates[1] as dayjs.Dayjs,
                ]);
              } else {
                setDateRange(null);
              }
            }}
            placeholder={[
              t('scenes.leads.scene.export.placeholders.start_date'),
              t('scenes.leads.scene.export.placeholders.end_date'),
            ]}
            allowClear
          />
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: '4px', fontSize: '12px' }}
          >
            {t('scenes.leads.scene.export.helpers.date_optional')}
          </Text>
        </Col>

        <Col xs={24}>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            {t('scenes.leads.scene.export.labels.availability_date_range')}
          </Text>
          <RangePicker
            style={{ width: '100%' }}
            format={dateFormat}
            value={availabilityDateRange}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setAvailabilityDateRange([
                  dates[0] as dayjs.Dayjs,
                  dates[1] as dayjs.Dayjs,
                ]);
              } else {
                setAvailabilityDateRange(null);
              }
            }}
            placeholder={[
              t('scenes.leads.scene.export.placeholders.start_date'),
              t('scenes.leads.scene.export.placeholders.end_date'),
            ]}
            allowClear
          />
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: '4px', fontSize: '12px' }}
          >
            {t('scenes.leads.scene.export.helpers.availability_date_optional')}
          </Text>
        </Col>

        <Col xs={24}>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            {t('scenes.leads.scene.export.labels.statuses')}
          </Text>
          <Select
            mode="multiple"
            value={statusSelection}
            onChange={(value) =>
              setStatusSelection(value as ProfileStatusCode[])
            }
            options={statusOptions}
            placeholder={t('scenes.leads.scene.export.placeholders.statuses')}
            style={{ width: '100%' }}
            allowClear
            maxTagCount="responsive"
            disabled={statuses.length === 0}
          />
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: '4px', fontSize: '12px' }}
          >
            {t('scenes.leads.scene.export.helpers.statuses_optional')}
          </Text>
        </Col>

        <Col xs={24}>
          <LeadTagsFilter
            country={countrySelection}
            active={isOpen}
            value={tagSelection}
            match={tagMatch}
            onChange={setTagSelection}
            onMatchChange={setTagMatch}
            strongLabel
          />
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: '4px', fontSize: '12px' }}
          >
            {t('scenes.leads.scene.export.helpers.tags_optional')}
          </Text>
        </Col>

        {SHOW_LOCATION_FILTERS && (
          <>
            <Col xs={24}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                {t('scenes.leads.scene.export.labels.universities')}
              </Text>
              <Select
                mode="multiple"
                value={universitySelection}
                onChange={(value) => setUniversitySelection(value as number[])}
                loading={universitiesLoading}
                disabled={universitiesLoading}
                options={universityOptions}
                optionFilterProp="label"
                showSearch
                placeholder={t(
                  'scenes.leads.scene.filters.university_placeholder'
                )}
                style={{ width: '100%' }}
                allowClear
                maxTagCount="responsive"
              />
            </Col>

            <Col xs={24}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                {regionLabel}
              </Text>
              <Select
                value={regionSelection}
                onChange={(value) => handleRegionChange(value as string)}
                loading={regionsLoading}
                disabled={regionsLoading}
                options={regionOptions}
                optionFilterProp="label"
                showSearch
                placeholder={t('scenes.leads.scene.filters.region_placeholder')}
                style={{ width: '100%' }}
                allowClear
              />
            </Col>

            <Col xs={24}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                {cityLabel}
              </Text>
              <Select
                mode="multiple"
                value={citySelection}
                onChange={(value) => setCitySelection(value as number[])}
                // Mientras se recargan las regiones el catalogo de ciudades
                // tambien queda invalidado: cargan juntos.
                loading={citiesLoading || regionsLoading}
                disabled={!regionSelection || citiesLoading || regionsLoading}
                options={cityOptions}
                optionFilterProp="label"
                showSearch
                placeholder={
                  regionSelection
                    ? t('scenes.leads.scene.filters.city_placeholder')
                    : t(
                        'scenes.leads.scene.filters.city_placeholder_no_region',
                        { region: regionLabel }
                      )
                }
                style={{ width: '100%' }}
                allowClear
                maxTagCount="responsive"
              />
            </Col>
          </>
        )}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col xs={24}>
          <Button
            type="primary"
            block
            onClick={handleExport}
            loading={loading}
            disabled={!countrySelection}
            icon={<FileExcelOutlined />}
          >
            {t('scenes.leads.scene.export.buttons.export_users')}
          </Button>
        </Col>
      </Row>
    </Modal>
  );
}

export default ExportLeadsModal;
